import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Button, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Switch } from "antd";
import { useForm } from "antd/lib/form/Form";
import moment from "moment";
import { createMilestone, updateMilestone } from "../../../core/store/reducers/milestone.slice";
import { showNotification } from "../../../shared/utils/utils";

const INVOICE_STATUS_OPTIONS = [
  { value: "NOT DUE", label: "NOT DUE" },
  { value: "RAISED", label: "RAISED" },
  { value: "PAID", label: "PAID" },
];

interface Props {
  visible: boolean;
  onClose: () => void;
  projectId: string;
  selectedMilestone?: any | null;
  staffOptions: { value: string; label: string }[];
  onSuccess: () => void;
}

const CreateEditMilestoneComponent: React.FC<Props> = ({
  visible,
  onClose,
  projectId,
  selectedMilestone = null,
  staffOptions,
  onSuccess,
}: Props) => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const isEdit = !!selectedMilestone?.milestoneId;

  useEffect(() => {
    if (visible) {
      if (selectedMilestone) {
        form.setFieldsValue({
          name: selectedMilestone.name,
          spoc: selectedMilestone.spoc || undefined,
          percentage: selectedMilestone.percentage != null ? Number(selectedMilestone.percentage) : undefined,
          estimatedBillingDate: selectedMilestone.estimatedBillingDate
            ? moment(selectedMilestone.estimatedBillingDate)
            : undefined,
          invoiceStatus: selectedMilestone.invoiceStatus || undefined,
          invoiceDate: selectedMilestone.invoiceDate ? moment(selectedMilestone.invoiceDate) : undefined,
          invoiceDueDate: selectedMilestone.invoiceDueDate ? moment(selectedMilestone.invoiceDueDate) : undefined,
          invoiceNumber: selectedMilestone.invoiceNumber,
          invoiceRemarks: selectedMilestone.invoiceRemarks,
          paymentReceived: selectedMilestone.paymentReceived ?? false,
          paymentReceivedDate: selectedMilestone.paymentReceivedDate
            ? moment(selectedMilestone.paymentReceivedDate)
            : undefined,
          paymentReceivedRemarks: selectedMilestone.paymentReceivedRemarks,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ paymentReceived: false });
      }
    }
  }, [visible, selectedMilestone, form]);

  const onFinish = (values: any) => {
    const payload = {
      projectId,
      name: values.name?.trim(),
      spoc: values.spoc || null,
      percentage: values.percentage != null ? values.percentage : null,
      estimatedBillingDate: values.estimatedBillingDate
        ? moment(values.estimatedBillingDate).format("YYYY-MM-DD")
        : null,
      invoiceStatus: values.invoiceStatus || null,
      invoiceDate: values.invoiceDate ? moment(values.invoiceDate).format("YYYY-MM-DD") : null,
      invoiceDueDate: values.invoiceDueDate ? moment(values.invoiceDueDate).format("YYYY-MM-DD") : null,
      invoiceNumber: values.invoiceNumber || null,
      invoiceRemarks: values.invoiceRemarks || null,
      paymentReceived: Boolean(values.paymentReceived ?? false),
      paymentReceivedDate: values.paymentReceivedDate
        ? moment(values.paymentReceivedDate).format("YYYY-MM-DD")
        : null,
      paymentReceivedRemarks: values.paymentReceivedRemarks || null,
    };

    if (isEdit) {
      dispatch(
        updateMilestone({
          milestoneId: selectedMilestone.milestoneId,
          body: payload,
        })
      ).then((val: any) => {
        const data = val?.payload?.data;
        if (data?.statusCode === 200 || data?.data) {
          showNotification("Milestone updated successfully", "success");
          onSuccess();
          onClose();
        } else if (data?.message) {
          showNotification(data.message, "error");
        }
      });
    } else {
      dispatch(createMilestone(payload)).then((val: any) => {
        const data = val?.payload?.data;
        if (data?.statusCode === 200 || data?.data) {
          showNotification("Milestone created successfully", "success");
          onSuccess();
          onClose();
        } else if (data?.message) {
          showNotification(data.message, "error");
        }
      });
    }
  };

  return (
    <Modal
      className="app-form-modal"
      title={isEdit ? "Edit Milestone" : "Add Milestone"}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ paymentReceived: false }}
      >
        <Form.Item
          name="name"
          label="Milestone Name"
          rules={[{ required: true, message: "Please enter milestone name" }]}
        >
          <Input placeholder="Milestone name" />
        </Form.Item>

        <Form.Item name="spoc" label="SPOC (Single Point of Contact)">
          <Select
            allowClear
            showSearch
            placeholder="Select SPOC"
            options={staffOptions}
            filterOption={(input, option) =>
              (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item name="percentage" label="Completion / Billing %">
          <InputNumber min={0} max={100} style={{ width: "100%" }} placeholder="e.g. 25" />
        </Form.Item>

        <Form.Item name="estimatedBillingDate" label="Estimated Billing Date">
          <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="invoiceStatus" label="Invoice Status">
              <Select allowClear placeholder="Select status" options={INVOICE_STATUS_OPTIONS} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="invoiceNumber" label="Invoice Number">
              <Input placeholder="Invoice number" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="invoiceDate" label="Invoice Date">
              <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="invoiceDueDate" label="Due Date">
              <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="invoiceRemarks" label="Invoice Remarks">
          <Input.TextArea rows={2} placeholder="Invoice notes" />
        </Form.Item>

        <Form.Item name="paymentReceived" label="Payment Received" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.paymentReceived !== curr.paymentReceived}>
          {({ getFieldValue }) =>
            getFieldValue("paymentReceived") ? (
              <>
                <Form.Item name="paymentReceivedDate" label="Payment Received Date">
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                </Form.Item>
                <Form.Item name="paymentReceivedRemarks" label="Payment Remarks">
                  <Input.TextArea rows={2} placeholder="Payment notes" />
                </Form.Item>
              </>
            ) : null
          }
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
          <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
            {isEdit ? "Update" : "Create"}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateEditMilestoneComponent;
